'use client';

import { Fragment, useEffect, useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

const SLIDE_LABELS: Record<string, string> = {
  cover: '1. Cover',
  problem: '2. Problem',
  solution: '3. Solution',
  'how-it-works': '4. How it works',
  possible: '5. Possible',
  'why-us': '6. Why us',
  built: '7. Built',
  business: '8. Business',
  gtm: '9. GTM',
  team: '10. Team',
  ask: '11. Ask',
  closer: '12. Closer',
};

const SLIDE_ORDER = [
  'cover',
  'problem',
  'solution',
  'how-it-works',
  'possible',
  'why-us',
  'built',
  'business',
  'gtm',
  'team',
  'ask',
  'closer',
];

interface StatsResponse {
  share: { id: string; code: string; label: string | null; createdAt: string; expiresAt: string | null };
  totals: {
    sessions: number;
    opens: number;
    completes: number;
    completionRate: number;
    composerSubmits: number;
    ctaClicks: number;
    prints: number;
    linkClicks: number;
    totalDwellMs: number;
  };
  slides: {
    slide: string;
    views: number;
    uniqueSessions: number;
    revisits: number;
    scrollBacks: number;
    totalDwellMs: number;
    avgDwellMs: number;
    medianDwellMs: number;
  }[];
  links: { href: string; slide: string | null; clicks: number; uniqueSessions: number }[];
  geo: { country: string; sessions: number }[];
  sessions: {
    sessionId: string;
    firstSeen: string;
    lastSeen: string;
    country: string | null;
    city: string | null;
    region: string | null;
    timezone: string | null;
    userAgent: string | null;
    ip: string | null;
    slidesViewed: number;
    maxSlideIndex: number;
    completed: boolean;
    totalDwellMs: number;
    scrollBacks: number;
    links: number;
    timeline: {
      ts: string;
      action: string;
      slide: string | null;
      slideIndex: number | null;
      dwellMs: number | null;
      href: string | null;
      scrollBack: boolean;
      revisit: boolean;
    }[];
  }[];
}

function fmtDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  if (m < 60) return `${m}m ${rs}s`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return `${h}h ${rm}m`;
}

function uaFamily(ua: string | null): string {
  if (!ua) return '—';
  if (/iPhone|iPad/.test(ua)) return 'iOS';
  if (/Android/.test(ua)) return 'Android';
  if (/Edg\//.test(ua)) return 'Edge';
  if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) return 'Chrome';
  if (/Safari\//.test(ua) && !/Chrome/.test(ua)) return 'Safari';
  if (/Firefox\//.test(ua)) return 'Firefox';
  return 'Other';
}

function osFamily(ua: string | null): string {
  if (!ua) return '';
  if (/iPhone OS|iPad/.test(ua)) return 'iOS';
  if (/Android/.test(ua)) return 'Android';
  if (/Mac OS X/.test(ua)) return 'macOS';
  if (/Windows NT/.test(ua)) return 'Windows';
  if (/Linux/.test(ua)) return 'Linux';
  return '';
}

function fmtGeo(s: { city: string | null; region: string | null; country: string | null }): string {
  const parts = [s.city, s.region, s.country].filter(Boolean) as string[];
  if (parts.length === 0) return '—';
  // De-dupe when region == city (some edges echo).
  const seen = new Set<string>();
  return parts.filter((p) => (seen.has(p.toLowerCase()) ? false : (seen.add(p.toLowerCase()), true))).join(', ');
}

export function DeckShareStats({ code }: { code: string }) {
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSession, setExpandedSession] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`/api/admin/deck/share/${encodeURIComponent(code)}/stats`)
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data?.error || `HTTP ${res.status}`);
        }
        return res.json() as Promise<StatsResponse>;
      })
      .then((data) => {
        if (!cancelled) setStats(data);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [code]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner size="lg" tone="muted" />
      </div>
    );
  }
  if (error) {
    return <p className="text-sm text-destructive px-4 py-3">Failed to load stats: {error}</p>;
  }
  if (!stats) return null;

  const orderedSlides = SLIDE_ORDER
    .map((id) => stats.slides.find((s) => s.slide === id))
    .filter((s): s is StatsResponse['slides'][number] => Boolean(s));
  const extraSlides = stats.slides.filter((s) => !SLIDE_ORDER.includes(s.slide));
  const allSlides = [...orderedSlides, ...extraSlides];
  const maxAvg = Math.max(1, ...allSlides.map((s) => s.avgDwellMs));
  const maxLinkClicks = Math.max(1, ...stats.links.map((l) => l.clicks));
  const maxGeoSessions = Math.max(1, ...stats.geo.map((g) => g.sessions));

  return (
    <div className="space-y-6 px-4 py-5 bg-muted/20">
      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kpi label="Sessions" value={stats.totals.sessions} />
        <Kpi label="Opens" value={stats.totals.opens} />
        <Kpi
          label="Completion"
          value={`${Math.round(stats.totals.completionRate * 100)}%`}
          sub={`${stats.totals.completes} / ${stats.totals.sessions}`}
        />
        <Kpi label="Total time" value={fmtDuration(stats.totals.totalDwellMs)} />
        <Kpi label="Link clicks" value={stats.totals.linkClicks} />
        <Kpi label="Composer submits" value={stats.totals.composerSubmits} />
        <Kpi label="Chip clicks" value={stats.totals.ctaClicks} />
        <Kpi label="PDF opens" value={stats.totals.prints} />
      </div>

      {/* Slide engagement */}
      <Section title="Time per slide">
        {allSlides.length === 0 ? (
          <Empty>No slide views yet.</Empty>
        ) : (
          <div className="space-y-1">
            {allSlides.map((s) => {
              const ratio = s.avgDwellMs / maxAvg;
              return (
                <div key={s.slide} className="grid grid-cols-[180px_1fr_auto] items-center gap-3 text-xs">
                  <span className="text-muted-foreground truncate">
                    {SLIDE_LABELS[s.slide] ?? s.slide}
                  </span>
                  <div className="relative h-5 rounded bg-muted overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 bg-primary/70"
                      style={{ width: `${Math.max(2, Math.round(ratio * 100))}%` }}
                    />
                    <span className="absolute inset-y-0 left-2 flex items-center text-[10px] tabular-nums text-foreground/80">
                      {fmtDuration(s.avgDwellMs)} avg
                      {s.medianDwellMs ? ` · ${fmtDuration(s.medianDwellMs)} med` : ''}
                    </span>
                  </div>
                  <span className="tabular-nums text-muted-foreground whitespace-nowrap">
                    {s.uniqueSessions}× sess · {s.views} views
                    {s.scrollBacks ? ` · ${s.scrollBacks} ↩` : ''}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Geo */}
        <Section title="Geo (by session)">
          {stats.geo.length === 0 ? (
            <Empty>No country data yet.</Empty>
          ) : (
            <div className="space-y-1">
              {stats.geo.map((g) => {
                // Surface a comma-list of distinct cities seen for this country
                // so the dashboard goes one level finer than country-only.
                const citiesInCountry = Array.from(
                  new Set(
                    stats.sessions
                      .filter((s) => s.country === g.country && s.city)
                      .map((s) => s.city as string),
                  ),
                );
                return (
                  <div key={g.country} className="grid grid-cols-[60px_1fr_auto] items-center gap-3 text-xs">
                    <span className="font-mono">{g.country}</span>
                    <div>
                      <div className="relative h-4 rounded bg-muted overflow-hidden">
                        <div
                          className="absolute inset-y-0 left-0 bg-emerald-500/60"
                          style={{ width: `${Math.round((g.sessions / maxGeoSessions) * 100)}%` }}
                        />
                      </div>
                      {citiesInCountry.length > 0 && (
                        <div className="text-muted-foreground text-[10px] mt-0.5 truncate">
                          {citiesInCountry.join(' · ')}
                        </div>
                      )}
                    </div>
                    <span className="tabular-nums text-muted-foreground">{g.sessions}</span>
                  </div>
                );
              })}
            </div>
          )}
        </Section>

        {/* Link clicks */}
        <Section title="Link clicks">
          {stats.links.length === 0 ? (
            <Empty>No link clicks yet.</Empty>
          ) : (
            <div className="space-y-1">
              {stats.links.map((l) => (
                <div
                  key={`${l.slide ?? ''}|${l.href}`}
                  className="grid grid-cols-[1fr_auto] items-center gap-3 text-xs"
                >
                  <div className="min-w-0">
                    <a
                      href={l.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="truncate text-foreground hover:text-primary block"
                      title={l.href}
                    >
                      {l.href}
                    </a>
                    <span className="text-muted-foreground">
                      {l.slide ? SLIDE_LABELS[l.slide] ?? l.slide : 'unknown slide'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    <span className="tabular-nums">{l.clicks}</span>
                    <div className="w-16 h-2 rounded bg-muted overflow-hidden">
                      <div
                        className="h-full bg-primary/70"
                        style={{ width: `${Math.round((l.clicks / maxLinkClicks) * 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      {/* Sessions */}
      <Section title="Sessions" badge={`${stats.sessions.length}`}>
        {stats.sessions.length === 0 ? (
          <Empty>No sessions yet.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-muted-foreground uppercase tracking-wider">
                <tr>
                  <th className="w-6 py-1.5 px-1" />
                  <th className="text-left py-1.5 px-2">First seen</th>
                  <th className="text-left py-1.5 px-2">Location</th>
                  <th className="text-left py-1.5 px-2">UA · OS</th>
                  <th className="text-right py-1.5 px-2">Slides</th>
                  <th className="text-right py-1.5 px-2">Time</th>
                  <th className="text-center py-1.5 px-2">Done</th>
                  <th className="text-right py-1.5 px-2">Links</th>
                  <th className="text-left py-1.5 px-2">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {stats.sessions.map((s) => {
                  const isOpen = expandedSession === s.sessionId;
                  const ChevronIcon = isOpen ? ChevronDown : ChevronRight;
                  return (
                    <Fragment key={s.sessionId}>
                      <tr
                        className={cn('hover:bg-muted/30 cursor-pointer', isOpen && 'bg-muted/30')}
                        onClick={() => setExpandedSession(isOpen ? null : s.sessionId)}
                      >
                        <td className="py-1.5 px-1 text-muted-foreground">
                          <ChevronIcon className="h-3.5 w-3.5" />
                        </td>
                        <td className="py-1.5 px-2 text-muted-foreground whitespace-nowrap">
                          {new Date(s.firstSeen).toLocaleString()}
                        </td>
                        <td className="py-1.5 px-2">
                          <span title={s.timezone ? `tz: ${s.timezone}` : undefined}>
                            {fmtGeo(s)}
                          </span>
                        </td>
                        <td className="py-1.5 px-2 text-muted-foreground">
                          {uaFamily(s.userAgent)}
                          {osFamily(s.userAgent) ? ` · ${osFamily(s.userAgent)}` : ''}
                        </td>
                        <td className="py-1.5 px-2 text-right tabular-nums">{s.slidesViewed}/12</td>
                        <td className="py-1.5 px-2 text-right tabular-nums text-muted-foreground">
                          {fmtDuration(s.totalDwellMs)}
                        </td>
                        <td className="py-1.5 px-2 text-center">
                          {s.completed ? (
                            <span className="text-emerald-500">✓</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="py-1.5 px-2 text-right tabular-nums">{s.links || '—'}</td>
                        <td className="py-1.5 px-2 font-mono text-muted-foreground">{s.ip ?? '—'}</td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-muted/20">
                          <td colSpan={9} className="px-3 py-3">
                            <SessionDetail session={s} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}

function Kpi({ label, value, sub }: { label: string; value: number | string; sub?: string }) {
  return (
    <div className="rounded-lg border border-border bg-background px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-base font-semibold tabular-nums mt-0.5">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5 tabular-nums">{sub}</div>}
    </div>
  );
}

function Section({
  title,
  badge,
  children,
}: {
  title: string;
  badge?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <h4 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
          {title}
        </h4>
        {badge && (
          <span className="text-[10px] tabular-nums text-muted-foreground bg-muted/50 rounded px-1.5 py-0.5">
            {badge}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-muted-foreground italic">{children}</p>;
}

function Legend({ swatch, label, thin }: { swatch: string; label: string; thin?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={cn('rounded-sm', swatch, thin ? 'w-[2px] h-3' : 'w-3 h-3')}
      />
      <span>{label}</span>
    </span>
  );
}

function SessionDetail({ session: s }: { session: StatsResponse['sessions'][number] }) {
  // Per-slide aggregation for THIS session only.
  const perSlide = new Map<string, { views: number; dwellMs: number; scrollBacks: number }>();
  const perLink = new Map<string, { href: string; slide: string | null; clicks: number }>();
  for (const ev of s.timeline) {
    if (ev.action === 'deck.slide_view' && ev.slide) {
      const r = perSlide.get(ev.slide) ?? { views: 0, dwellMs: 0, scrollBacks: 0 };
      r.views++;
      perSlide.set(ev.slide, r);
    }
    if (ev.action === 'deck.slide_dwell' && ev.slide && ev.dwellMs != null) {
      const r = perSlide.get(ev.slide) ?? { views: 0, dwellMs: 0, scrollBacks: 0 };
      r.dwellMs += ev.dwellMs;
      if (ev.scrollBack) r.scrollBacks++;
      perSlide.set(ev.slide, r);
    }
    if (ev.action === 'deck.link_click' && ev.href) {
      const key = `${ev.slide ?? ''}|${ev.href}`;
      const r = perLink.get(key) ?? { href: ev.href, slide: ev.slide, clicks: 0 };
      r.clicks++;
      perLink.set(key, r);
    }
  }
  const orderedSlides = SLIDE_ORDER
    .map((id) => perSlide.has(id) ? { slide: id, ...perSlide.get(id)! } : null)
    .filter((s): s is { slide: string; views: number; dwellMs: number; scrollBacks: number } => Boolean(s));
  const maxDwell = Math.max(1, ...orderedSlides.map((s) => s.dwellMs));

  const firstTs = +new Date(s.firstSeen);
  const lastTs = +new Date(s.lastSeen);
  const totalSpan = Math.max(1000, lastTs - firstTs);

  // Build Gantt segments — one bar per contiguous time the user spent on a
  // slide. A slide_view opens a segment; the next slide_view or the session
  // end closes it. Revisits naturally produce multiple bars on the same row.
  type Segment = {
    slide: string;
    startMs: number;
    endMs: number;
    revisit: boolean;
    scrollBack: boolean;
  };
  const segments: Segment[] = [];
  type Marker = {
    kind: 'link' | 'composer' | 'cta' | 'print' | 'open' | 'complete' | 'close';
    atMs: number;
    slide: string | null;
    label: string;
  };
  const markers: Marker[] = [];

  let current: { slide: string; startTs: number; revisit: boolean } | null = null;
  const closeCurrent = (endTs: number, scrollBack = false) => {
    if (!current) return;
    segments.push({
      slide: current.slide,
      startMs: Math.max(0, current.startTs - firstTs),
      endMs: Math.max(current.startTs - firstTs + 250, endTs - firstTs),
      revisit: current.revisit,
      scrollBack,
    });
    current = null;
  };

  for (const ev of s.timeline) {
    const ts = +new Date(ev.ts);
    if (ev.action === 'deck.slide_view' && ev.slide) {
      closeCurrent(ts);
      current = { slide: ev.slide, startTs: ts, revisit: !!ev.revisit };
    } else if (ev.action === 'deck.slide_dwell' && ev.slide && ev.dwellMs != null) {
      // A dwell event tells us how long the slide we just left (or are still
      // on) was active. If it pertains to the currently-open segment, close
      // it at startTs + dwellMs; otherwise emit a standalone segment.
      if (current && current.slide === ev.slide) {
        const endTs = current.startTs + (ev.dwellMs ?? 0);
        closeCurrent(endTs, !!ev.scrollBack);
      } else {
        const endTs = ts;
        const startTs = ts - (ev.dwellMs ?? 0);
        segments.push({
          slide: ev.slide,
          startMs: Math.max(0, startTs - firstTs),
          endMs: Math.max(startTs - firstTs + 250, endTs - firstTs),
          revisit: !!ev.revisit,
          scrollBack: !!ev.scrollBack,
        });
      }
    } else if (ev.action === 'deck.close' || ev.action === 'deck.complete') {
      closeCurrent(ts);
      markers.push({
        kind: ev.action === 'deck.complete' ? 'complete' : 'close',
        atMs: ts - firstTs,
        slide: ev.slide,
        label: ev.action === 'deck.complete' ? 'Reached the end' : 'Left',
      });
    } else if (ev.action === 'deck.open') {
      markers.push({ kind: 'open', atMs: ts - firstTs, slide: ev.slide, label: 'Opened' });
    } else if (ev.action === 'deck.link_click' && ev.href) {
      markers.push({ kind: 'link', atMs: ts - firstTs, slide: ev.slide, label: ev.href });
    } else if (ev.action === 'deck.composer_submit') {
      markers.push({ kind: 'composer', atMs: ts - firstTs, slide: ev.slide, label: 'Composer submit' });
    } else if (ev.action === 'deck.cta_click') {
      markers.push({ kind: 'cta', atMs: ts - firstTs, slide: ev.slide, label: 'Chip click' });
    } else if (ev.action === 'deck.print_open') {
      markers.push({ kind: 'print', atMs: ts - firstTs, slide: ev.slide, label: 'PDF print' });
    }
  }
  // If the session never emitted a close/complete event, the last slide bar
  // runs through to lastSeen so it's visible.
  if (current) closeCurrent(lastTs);

  // Y-axis only includes slides that were actually visited in this session.
  const visitedSlides = SLIDE_ORDER.filter((id) => perSlide.has(id));

  // X-axis tick marks — 5 evenly spaced labels across the session.
  const tickCount = 4;
  const ticks = Array.from({ length: tickCount + 1 }, (_, i) => ({
    pct: (i / tickCount) * 100,
    label: fmtDuration(Math.round((i / tickCount) * totalSpan)),
  }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div>
        <h5 className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">
          Per-slide engagement
        </h5>
        {orderedSlides.length === 0 ? (
          <Empty>No slide views yet.</Empty>
        ) : (
          <div className="space-y-1">
            {orderedSlides.map((row) => (
              <div key={row.slide} className="grid grid-cols-[140px_1fr_auto] items-center gap-3 text-xs">
                <span className="text-muted-foreground truncate">
                  {SLIDE_LABELS[row.slide] ?? row.slide}
                </span>
                <div className="relative h-4 rounded bg-muted overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 bg-primary/70"
                    style={{ width: `${Math.max(2, Math.round((row.dwellMs / maxDwell) * 100))}%` }}
                  />
                </div>
                <span className="tabular-nums text-muted-foreground whitespace-nowrap">
                  {fmtDuration(row.dwellMs)}
                  {row.scrollBacks ? ` · ${row.scrollBacks} ↩` : ''}
                </span>
              </div>
            ))}
          </div>
        )}

        {perLink.size > 0 && (
          <>
            <h5 className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mt-4 mb-2">
              Link clicks ({[...perLink.values()].reduce((a, b) => a + b.clicks, 0)})
            </h5>
            <div className="space-y-1">
              {[...perLink.values()]
                .sort((a, b) => b.clicks - a.clicks)
                .map((l) => (
                  <div key={`${l.slide ?? ''}|${l.href}`} className="text-xs">
                    <a
                      href={l.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="truncate text-foreground hover:text-primary block"
                      title={l.href}
                    >
                      {l.href}
                    </a>
                    <span className="text-muted-foreground">
                      {(l.slide ? SLIDE_LABELS[l.slide] ?? l.slide : 'unknown slide')}
                      {l.clicks > 1 ? ` · ${l.clicks}×` : ''}
                    </span>
                  </div>
                ))}
            </div>
          </>
        )}
      </div>

      <div>
        <h5 className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">
          Timeline ({fmtDuration(totalSpan)})
        </h5>
        {visitedSlides.length === 0 ? (
          <Empty>No timeline yet.</Empty>
        ) : (
          <div className="text-xs">
            <div className="grid grid-cols-[140px_1fr] gap-2">
              <div />
              <div className="relative h-3 mb-1">
                {ticks.map((tk, i) => (
                  <span
                    key={i}
                    className="absolute top-0 -translate-x-1/2 font-mono tabular-nums text-[10px] text-muted-foreground"
                    style={{ left: `${tk.pct}%` }}
                  >
                    {tk.label}
                  </span>
                ))}
              </div>
            </div>
            <div className="space-y-[3px]">
              {visitedSlides.map((slide) => {
                const rowSegments = segments.filter((seg) => seg.slide === slide);
                const rowMarkers = markers.filter((m) => m.slide === slide);
                return (
                  <div key={slide} className="grid grid-cols-[140px_1fr] items-center gap-2">
                    <span className="text-muted-foreground truncate">
                      {SLIDE_LABELS[slide] ?? slide}
                    </span>
                    <div className="relative h-5 rounded bg-muted/40 border border-border/40">
                      {ticks.map((tk, i) => (
                        <span
                          key={i}
                          className="absolute inset-y-0 w-px bg-border/40"
                          style={{ left: `${tk.pct}%` }}
                        />
                      ))}
                      {rowSegments.map((seg, i) => {
                        const left = (seg.startMs / totalSpan) * 100;
                        const width = Math.max(0.5, ((seg.endMs - seg.startMs) / totalSpan) * 100);
                        const dur = seg.endMs - seg.startMs;
                        return (
                          <div
                            key={i}
                            className={cn(
                              'absolute inset-y-0 rounded-sm flex items-center justify-end px-1 overflow-hidden whitespace-nowrap text-[10px] tabular-nums',
                              seg.revisit
                                ? 'bg-amber-500/70 text-amber-50'
                                : 'bg-primary/70 text-primary-foreground',
                              seg.scrollBack && 'ring-1 ring-amber-400/70',
                            )}
                            style={{ left: `${left}%`, width: `${width}%` }}
                            title={`${SLIDE_LABELS[slide] ?? slide} · ${fmtDuration(dur)}${seg.revisit ? ' · revisit' : ''}${seg.scrollBack ? ' · scroll-back' : ''}`}
                          >
                            {width > 6 ? fmtDuration(dur) : ''}
                          </div>
                        );
                      })}
                      {rowMarkers.map((m, i) => {
                        const left = (m.atMs / totalSpan) * 100;
                        const color =
                          m.kind === 'link'
                            ? 'bg-amber-500'
                            : m.kind === 'composer' || m.kind === 'cta'
                              ? 'bg-purple-500'
                              : m.kind === 'print'
                                ? 'bg-blue-500'
                                : m.kind === 'complete'
                                  ? 'bg-emerald-500'
                                  : 'bg-foreground/70';
                        return (
                          <span
                            key={`m${i}`}
                            className={cn(
                              'absolute -top-1 bottom-[-4px] w-[2px] -translate-x-1/2 rounded-full',
                              color,
                            )}
                            style={{ left: `${left}%` }}
                            title={`${m.kind} · ${m.label}`}
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-3 text-[10px] text-muted-foreground">
              <Legend swatch="bg-primary/70" label="Time on slide" />
              <Legend swatch="bg-amber-500/70" label="Revisit" />
              <Legend swatch="bg-amber-500" thin label="Link click" />
              <Legend swatch="bg-purple-500" thin label="Composer / chip" />
              <Legend swatch="bg-blue-500" thin label="PDF print" />
              <Legend swatch="bg-emerald-500" thin label="Reached end" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DeckShareStats;
